import { Action, Reducer } from "redux";
import { AppThunkAction, remoteActions } from ".";
import { DeepClone, RemoveDuplicates, GetIntFromString } from "../helperFunctions";
import { InsuranceState, InsurancePeriod, CoveragePrice } from "./Insurance";

export const ADDON_CHANGED = "ADDON_CHANGED";
export const ADDONS_COVERAGE_CHANGED = "ADDONS_COVERAGE_CHANGED";
export const MILLAGE_UNDER_150000_CHANGED = "MILLAGE_UNDER_150000_CHANGED";
export const NO_ADDONS_COVERAGE_CHANGED = "NO_ADDONS_COVERAGE_CHANGED";
export const CHECK_LOW_MILAGE = "CHECK_LOW_MILAGE";

export interface AddOnTexts {
  header: string;
  bodyText: string;
  price: string;
  readMore: string;
  readMorePath: string;
  addAddOn: string;
  dropAddOn: string;
  popupTitle: string;
  popupText: string;
  includedWithText: string;
}

export interface AddOnState {
  selectedAddOnInternalCodes: string[];
  addOns?: IAddOn[];
  title?: string;
}

export interface PopupState {
  imageUrl?: string;
  altText?: string;
  title?: string;
  description?: string;
  internalAddonCode?: string;
}

export interface UspState {
  body?: string;
  readMoreText?: string;
}

export interface IAddOn {
  Description: string;
  Exclude: string[];
  MonthlyPrice: string;
  Name: string;
  PopupText: string;
  SortOrder: number;
  VariablePrice: string;
  InternalCode: string;
  // additional  field for front-end
  Checked?: boolean;
  texts?: AddOnTexts;
  checkMark?: {};
  visible: boolean;
  disabled: boolean;
  campaigned: boolean;
  lowMillage?: LowMillage;
  PopupState?: PopupState;
  UspState?: UspState;
  includedWith?: IAddOn;
}

export interface LowMillage {
  questionVisible?: boolean;
  selected: boolean;
  text: string;
  requiredErrorMessage?: string;
  showError: boolean;
}

const defaults: AddOnState = {
  addOns: [
    {
      texts: {
        price: "25 Kr/md",
        header: "Extra",
        bodyText: "This is test addon for testing puropses",
        addAddOn: "Lägg till",
        dropAddOn: "Ta bort",
        readMore: "Läs mer.",
        readMorePath: "https://polestar.se",
        popupTitle: "Titile",
        popupText: "this is text for testing puropses",
        includedWithText: "Included with",
      },
      Description: "Descriptoin",
      Exclude: [],
      MonthlyPrice: "10 Kr/md",
      Name: "Extra",
      PopupText: "sadasfasdgf",
      SortOrder: 1,
      VariablePrice: "100",
      Checked: false,
      checkMark: {},
      InternalCode: "DDD",
      visible: true,
      disabled: false,
      campaigned: false,
      lowMillage: {
        questionVisible: false,
        selected: false,
        text: "This is Addon additional question for testing purposes",
        showError: false,
      },
    },
    {
      texts: {
        price: "25 Kr/md",
        header: "Extra",
        bodyText: "This is test addon for testing puropses",
        addAddOn: "Lägg till",
        dropAddOn: "Ta bort",
        readMore: "Läs mer.",
        readMorePath: "https://polestar.se",
        popupTitle: "Titile",
        popupText: "this is text for testing puropses",
        includedWithText: "Included with",
      },
      Description: "Descriptoin",
      Exclude: [],
      InternalCode: "DDD",
      MonthlyPrice: "10 Kr/md",
      Name: "Extra",
      PopupText: "sadasfasdgf",
      SortOrder: 2,
      VariablePrice: "100",
      Checked: false,
      checkMark: {},
      visible: true,
      disabled: false,
      campaigned: false,
      lowMillage: {
        questionVisible: true,
        selected: false,
        text: "This is Addon additional question for testing purposes",
        showError: false,
        requiredErrorMessage: "Error message!!!",
      },
    },
  ],
  selectedAddOnInternalCodes: [],
  title: "Addons",
};

export type AddOnAction = {
  type:
    | typeof ADDON_CHANGED
    | typeof ADDONS_COVERAGE_CHANGED
    | typeof MILLAGE_UNDER_150000_CHANGED
    | typeof NO_ADDONS_COVERAGE_CHANGED
    | typeof CHECK_LOW_MILAGE;
  data: any;
};

export interface IAddOnActionData {
  addOnInternalCode: string;
  newState: boolean;
}

export const reducer: Reducer<AddOnState> = (state: AddOnState = defaults, input: Action): AddOnState => {
  const action = input as AddOnAction;

  switch (action?.type) {
    case ADDON_CHANGED:
      return handleAddOnChange(state, action.data as IAddOnActionData);
    case ADDONS_COVERAGE_CHANGED:
      return handleCoverageChanged(state, action.data as string[]);
    case NO_ADDONS_COVERAGE_CHANGED:
      return handleNOCoverageChanged(state, action.data as IAddOn[]);
    case MILLAGE_UNDER_150000_CHANGED:
      return handleMillageQuestionChange(state, action.data as IAddOnActionData);
    case CHECK_LOW_MILAGE:
      return checkMillageQuestion(state, action.data as IAddOnActionData);
    default:
      return state;
  }
};

function handleAddOnChange(state: AddOnState, selectedAddonData?: IAddOnActionData): AddOnState {
  const newAddons = DeepClone(state?.addOns);

  const addOn = getAddOn(state, selectedAddonData?.addOnInternalCode);
  let newSelectedAddons: string[] = state?.selectedAddOnInternalCodes ?? [];

  if (addOn && newAddons) {
    addOn.Checked = selectedAddonData?.newState;

    const index = newAddons?.findIndex(x => x.InternalCode === addOn?.InternalCode);
    newAddons[index] = addOn;

    if (selectedAddonData?.addOnInternalCode) {
      if (addOn.Checked) {
        const includedAddons = addOn.Exclude;
        // remove excluded addons from selected list
        newSelectedAddons = newSelectedAddons.filter(item => !includedAddons.includes(item));
        // ADD selected addon
        newSelectedAddons.push(selectedAddonData.addOnInternalCode);
      } else {
        newSelectedAddons = newSelectedAddons.filter(item => item !== selectedAddonData.addOnInternalCode) ?? [];
      }
    }

    // filter out duplicates
    newSelectedAddons = RemoveDuplicates(newSelectedAddons);
  }

  return {
    ...state,
    addOns: newAddons,
    selectedAddOnInternalCodes: newSelectedAddons,
  };
}

export const checkMillageQuestion = (state: AddOnState, selectedAddonData?: IAddOnActionData): AddOnState => {
  const addOn = getAddOn(state, selectedAddonData?.addOnInternalCode);

  if (addOn?.Checked && addOn.lowMillage?.selected) {
    return state;
  }

  const newAddons = DeepClone(state?.addOns);

  if (addOn?.lowMillage && newAddons) {
    addOn.lowMillage = {
      ...addOn.lowMillage,
      showError: true,
      selected: false,
    };

    const index = newAddons?.findIndex(x => x.InternalCode === addOn?.InternalCode);
    newAddons[index] = addOn;
  }

  return {
    ...state,
    addOns: newAddons,
  };
};

function handleMillageQuestionChange(state: AddOnState, selectedAddonData?: IAddOnActionData): AddOnState {
  const newAddons = DeepClone(state?.addOns);

  const addOn = getAddOn(state, selectedAddonData?.addOnInternalCode);

  if (addOn?.lowMillage && newAddons) {
    addOn.lowMillage = { ...addOn.lowMillage, selected: selectedAddonData?.newState ?? false };

    const index = newAddons?.findIndex(x => x.InternalCode === addOn?.InternalCode);
    newAddons[index] = addOn;
  }

  return {
    ...state,
    addOns: newAddons,
  };
}

function getAddOn(state: AddOnState, internalCode?: string): IAddOn | undefined {
  let result: IAddOn | undefined;
  const addOnIndex = state.addOns?.findIndex(addon => addon.InternalCode === internalCode) ?? -1;
  if (state.addOns && addOnIndex > -1) {
    return state.addOns[addOnIndex];
  }
  return result;
}

function handleNOCoverageChanged(state: AddOnState, addons: IAddOn[]): AddOnState {
  const selectedAddonInternalCodes = state.selectedAddOnInternalCodes ?? [];

  const list =
    addons?.filter(i => i.InternalCode && selectedAddonInternalCodes?.indexOf(i.InternalCode) !== -1).map(i => i.InternalCode as string) ??
    [];

  addons.forEach(i => (i.Checked = !!i.InternalCode && list.indexOf(i.InternalCode) !== -1));

  return { ...state, selectedAddOnInternalCodes: list, addOns: addons };
}

function handleCoverageChanged(state: AddOnState, visibleAddons: string[]): AddOnState {
  /*
    When Customer is switching trough coverages we are saving his selected addons,
    but in case if customer changes to coverage where H3 is not present we must allow customer to pick available addons on that particular coverage.
    Solution here is when changing coverage always uncheck H3 addon and exclude from selected list!
  */

  const result = DeepClone(state.addOns);
  const addonsSelected = state.selectedAddOnInternalCodes;
  result?.forEach(addon => {
    if (addon.InternalCode === "H3") {
      addon.Checked = false;
      const index = addonsSelected.indexOf(addon.InternalCode ?? "");
      if (index !== -1) {
        addonsSelected.splice(index, 1);
      }
    }
  });

  const addons = SetAddonVisibility(result as IAddOn[], visibleAddons, addonsSelected);
  const selectedAddons: string[] = [];
  addons.forEach(x => {
    if (x.Checked) {
      selectedAddons.push(x.InternalCode ?? "");
    }
  });

  return { ...state, addOns: addons, selectedAddOnInternalCodes: selectedAddons };
}

/**
 * Sets `coverageAddons` prices based on passed `coveragePrice.Period`, and returns sum of addons multiplied for `coveragePrice.Period`
 * @param coveragePrice Coverage price and period
 * @param coverageAddons Addons that should be counted
 * @returns Sum of addons
 */
export function SetAddonPrices(coveragePrice: CoveragePrice, coverageAddons: IAddOn[]): number {
  const period = coveragePrice.Period;
  // adjust addon prices based on payment period
  coverageAddons.forEach(x => {
    let priceText = x?.texts?.price ?? x.MonthlyPrice;
    // Polestar already has correct price (and period) as MonthlyPrice string
    // so - if we have correct period in string, lets skip price adjusting
    if (priceText.indexOf(period) !== -1) {
      return;
    }

    if (period === InsurancePeriod.sixMonth || period === InsurancePeriod.year) {
      const oldPrice = GetIntFromString(priceText);
      const newPrice = oldPrice * (period === InsurancePeriod.sixMonth ? 6 : 12);

      priceText = priceText.replace(oldPrice.toString(), newPrice.toString());
      priceText = priceText.replace(InsurancePeriod.month, period);
    }
    x.MonthlyPrice = priceText;
    if (x.texts) {
      x.texts.price = priceText;
    }
  });
  const addonSum = coverageAddons.map(x => GetIntFromString(x?.texts?.price ?? x.MonthlyPrice)).reduce((a, b) => a + b, 0);
  return addonSum;
}

export function SetAddonVisibility(addons: IAddOn[], visibleAddons: string[], selectedAddons: string[]): IAddOn[] {
  const result = DeepClone(addons);

  result.forEach(x => {
    x.visible = visibleAddons?.includes(x.InternalCode ?? "") ?? false;
    x.Checked = selectedAddons?.includes(x.InternalCode ?? "");
  });

  const updatedAddons = PrepareSelectedAddons(result, selectedAddons);

  return updatedAddons;
}

export function PrepareAddonTexts(addons: IAddOn[]): IAddOn[] {
  const result = DeepClone(addons);

  result.map(
    i =>
      (i.texts = {
        price: i.MonthlyPrice,
        header: i.Name,
        bodyText: i.Description,
        addAddOn: "Lägg till",
        dropAddOn: "Ta bort",
        readMore: "Läs mer.",
        readMorePath: "https://polestar.se",
        popupTitle: i.Name,
        popupText: i.PopupText,
        includedWithText: "Ingår i",
      }),
  );

  return result;
}

export function PrepareNorwayAddonTexts(addons: IAddOn[]): IAddOn[] {
  const result = DeepClone(addons);

  result.map(
    i =>
      (i.texts = {
        price: i.MonthlyPrice,
        header: i.Name,
        bodyText: i.Description,
        addAddOn: "Legg til",
        dropAddOn: "Ta bort",
        readMore: "",
        readMorePath: "",
        popupTitle: i.Name,
        popupText: i.PopupText,
        includedWithText: "Inngår i",
      }),
  );

  return result;
}

export function PrepareSelectedAddons(addons: IAddOn[], selectedAddonInternalCodes: string[]): IAddOn[] {
  const result = DeepClone(addons);
  result.forEach(addon => {
    addon.Checked = selectedAddonInternalCodes.includes(addon.InternalCode ?? "");
  });

  // NOTE! Logic copied from Webshop V1 (Angular), and modified syntax a bit to work here - commented out unexisting (for now) stuff, an replaced lodash with pure TS
  // this method checks which addons in what cases needs to be selected or deselected
  // 1) if addon is selected disable all exlcuded by this one
  // 2) if switched to another coverage and selected is not there enable all disabled
  // 3) if that case addon is selected - unselect all previous selected addons that are on other coverages and excluded by this one
  // 4) if common addon is selected or unselected - do not affect others

  const selectedAddons = result.filter(addon => addon.Checked && addon.visible);
  if (!selectedAddons.length) {
    result.forEach(addon => {
      // if (updateInvisible || addon.Showed || !addon.Campaigned) {
      addon.includedWith = undefined;
      if (addon.visible) {
        // keep for now
        // addon.Checked = false;
        addon.disabled = false;
      }
    });
    return result;
  }

  // enabled by default
  result.forEach(addon => {
    addon.disabled = false;
  });

  const getAddOnThatIncludesCurrent = (internalCode: string) => {
    const addOn = result.find(addOn => addOn.Exclude.includes(internalCode) && addOn.Checked);
    return addOn;
  };

  // exclude addons
  const excluded = result.filter(x => x.Checked).map(x => x.Exclude);
  const excludedAddonsInternalCodes = excluded.filter(arr => arr && arr.length > 0).flat();

  // If addons exist in Exclude. It should be disabled
  result.forEach(addon => {
    addon.includedWith = undefined;
    if (excludedAddonsInternalCodes.includes(addon.InternalCode)) {
      if (!addon.Checked) {
        addon.disabled = true;
        addon.includedWith = getAddOnThatIncludesCurrent(addon.InternalCode);
      } else {
        if (!addon.visible) {
          addon.Checked = false;
        }
      }
    }
  });

  // disable campaigned addons
  result.forEach(addon => {
    if (addon.campaigned) {
      addon.disabled = true;
    }
  });

  return result;
}

export function MapAddonPopups(addons: IAddOn[], popups: any[]) {
  if (popups) {
    const popupState = popups as PopupState[];
    addons.forEach(x => {
      const popup = popupState.find((y: any) => y.internalAddonCode === x.InternalCode);
      if (popup) {
        x.PopupState = popup;
      }
    });
  }
  return addons;
}

export function MapAddonUSP(addons: IAddOn[], addonUSPs: any[]) {
  if (addonUSPs) {
    const uspState = addonUSPs as UspState[];
    addons.forEach(x => {
      const addonUSP = uspState.find((y: any) => y.internalAddonCode === x.InternalCode);
      if (addonUSP) {
        x.UspState = addonUSP;
      }
    });
  }
  return addons;
}

export function GetSelectedAddonsForCoverage(addonsState: AddOnState | undefined, insuranceState: InsuranceState | undefined): string[] {
  const selectedCoverage =
    insuranceState?.coverage?.Coverages != null
      ? insuranceState?.coverage?.Coverages.find(elem => elem.InternalCode === insuranceState?.coverage?.selectedCode)
      : undefined;
  return addonsState?.selectedAddOnInternalCodes.filter(x => selectedCoverage?.CoverageAddons?.map(x => x.InternalCode).includes(x)) ?? [];
}

export const actions = {
  addonChanged:
    (value: IAddOnActionData): AppThunkAction<AddOnAction | AppThunkAction<{}>> =>
    dispatch => {
      dispatch({
        type: ADDON_CHANGED,
        data: value as IAddOnActionData,
      });

      dispatch(remoteActions.requestPriceData());
    },

  coverageChanged:
    (visibleAddons: string[]): AppThunkAction<AddOnAction | AppThunkAction<{}>> =>
    dispatch => {
      dispatch({
        type: ADDONS_COVERAGE_CHANGED,
        data: visibleAddons as string[],
      });
    },

  lowMillageValueChanged:
    (value: unknown): AppThunkAction<AddOnAction | AppThunkAction<{}>> =>
    dispatch => {
      dispatch({
        type: MILLAGE_UNDER_150000_CHANGED,
        data: value as IAddOnActionData,
      });
    },
};
